import { useIsIntersecting } from '@hooks/useIsIntersecting';
import type { TextFieldPropOnChange, TextFieldPropStatus, TextFieldPropValue } from 'components/FieldNumberFormat';
import { ModalDialog } from 'components/ModalDialog';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { Button, cnMixSpace } from 'uikit';

import cn from 'classnames';
import { useCallback, useRef, useState } from 'react';

import Styles from '../catalogCommonStyles.module.css';
import { CatalogEditValueInput } from '../CatalogEditValueInput';
import { TypeItem } from '../TypeItem/TypeItem';

import { useCheckIsUniqueName, useCreateOrEditType, useGetTypesData, useRemoveType } from './hooks';

type TCatalogOthersTypeProps = {
    onClose: () => void;
    methodology: ProjectMethodologyEnum;
};

const MAX_NAME_LENGTH = 255;

export const CatalogOthersType = ({ onClose, methodology }: TCatalogOthersTypeProps) => {
    const [isShowInput, setIsShowInput] = useState<boolean>(false);
    const [inputStatus, setInputStatus] = useState<TextFieldPropStatus | undefined>(undefined);
    const [newName, setNewName] = useState<TextFieldPropValue>(null);
    const [tooltipMsg, setTooltipMsg] = useState<string | null>('');

    const resetState = useCallback(() => {
        setTooltipMsg(null);
        setInputStatus(undefined);
    }, []);

    const handleChangeNewName = useCallback<TextFieldPropOnChange>(
        ({ value }) => {
            setNewName(value);
            resetState();
        },
        [resetState],
    );

    const handleRemoveInput = useCallback(() => {
        setNewName(null);
        setIsShowInput(false);
        resetState();
    }, [resetState]);

    const handlerOpenCreate = useCallback(() => {
        setIsShowInput(true);
    }, []);

    const { typesData, updateTypesData } = useGetTypesData({
        methodology,
    });

    const { isUniqueName } = useCheckIsUniqueName({
        name: newName ?? '',
        methodology,
    });

    const handleSubmit = useCreateOrEditType({
        onSuccess: useCallback(() => {
            handleRemoveInput();
            updateTypesData();
        }, [handleRemoveInput, updateTypesData]),
    });

    const getErrorMessage = useCallback(() => {
        const nameLength = newName?.trim().length ?? 0;

        if (!nameLength) {
            return 'Поле не может быть пустым';
        }

        if (nameLength > MAX_NAME_LENGTH) {
            return `Не более ${MAX_NAME_LENGTH} символов`;
        }

        if (!isUniqueName) {
            return 'Тип прочих с таким наименованием уже существует';
        }

        return null;
    }, [newName, isUniqueName]);

    const handleSave = useCallback(() => {
        const errorMsg = getErrorMessage();

        if (errorMsg) {
            setTooltipMsg(errorMsg);
            setInputStatus('alert');

            return;
        }

        if (!newName) {
            return;
        }

        handleSubmit({ methodology, name: newName });
    }, [newName, getErrorMessage, handleSubmit, methodology]);

    const listWrapperRef = useRef<HTMLDivElement>(null);
    const { refCallback, isIntersecting } = useIsIntersecting(listWrapperRef.current);

    return (
        <ModalDialog
            isOpen
            title="Типы прочих"
            onClose={onClose}
            bodyWidth={600}
            withBottomButtons={false}
            withTitleCloseButton
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                <div className={cnMixSpace({ mB: 'm' })}>
                    <Button label="Создать новый тип прочих" view="secondary" size="s" onClick={handlerOpenCreate} />
                </div>
                <div ref={listWrapperRef} className={cn(Styles.listWrapper, cnMixSpace({ p: 'm' }))}>
                    {isShowInput && (
                        <div ref={refCallback}>
                            <CatalogEditValueInput
                                value={newName}
                                onChange={handleChangeNewName}
                                onCancel={handleRemoveInput}
                                onSubmit={handleSave}
                                status={inputStatus}
                                tooltipMsg={tooltipMsg ?? ''}
                                inputPlaceholder="Введите наименование типа прочих"
                                isTooltipVisible={isIntersecting}
                            />
                        </div>
                    )}
                    {typesData?.map((item) => (
                        <TypeItem
                            listWrapper={listWrapperRef.current}
                            key={item.id}
                            item={item}
                            methodology={methodology}
                            updateTypesData={updateTypesData}
                            edit={useCreateOrEditType}
                            remove={useRemoveType}
                            checkIsUniqueName={useCheckIsUniqueName}
                            editTooltipMsg="Тип прочих с таким наименованием уже существует"
                            editPlaceholder="Введите наименование типа прочих"
                            removeHeader="Удаление типа прочих"
                            removeQuestion="Вы уверены, что хотите удалить тип прочих?"
                            cantDeleteTooltip="Тип прочих включен в формулу прочих затрат"
                        />
                    ))}
                </div>
            </div>
        </ModalDialog>
    );
};

CatalogOthersType.displayName = 'CatalogOthersType';
